/**
 * Add-step type menu (AR-328).
 *
 * Picking a type opens the step editor pre-filled with an example of that type,
 * so the shape of a new step is visible before anything is saved. New types are
 * config-driven: an entry added to ADD_STEP_TYPES renders here automatically,
 * and the placeholder row states that contract in the UI.
 */
import React from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ADD_STEP_TYPES, STEP_TYPE_STYLES, type StepTypeOption } from '@/lib/activationBuilder';

interface AddStepMenuProps {
  onPick: (type: StepTypeOption['type']) => void;
}

/**
 * Uncontrolled on purpose: Radix closes the menu on select, so there is no open
 * flag for the page to leave stale when the editor it opened is closed again.
 */
export const AddStepMenu: React.FC<AddStepMenuProps> = ({ onPick }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button size="sm" className="bg-orange-500 text-white hover:bg-orange-600">
        <Plus className="mr-1 h-3.5 w-3.5" /> Add step
        <ChevronDown className="ml-1 h-3.5 w-3.5" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-80 p-0">
      <DropdownMenuLabel className="px-4 pb-1 pt-3 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">
        Choose a step type — opens an example
      </DropdownMenuLabel>
      <div className="pb-1">
        {ADD_STEP_TYPES.map((option) => (
          <DropdownMenuItem
            key={option.type}
            textValue={option.name}
            onSelect={() => onPick(option.type)}
            className="flex items-start gap-3 px-4 py-2.5"
          >
            <span
              className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', STEP_TYPE_STYLES[option.type].dot)}
              aria-hidden
            />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                {option.name}
              </span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                {option.description}
              </span>
            </span>
          </DropdownMenuItem>
        ))}
      </div>
      <DropdownMenuSeparator />
      <p className="px-4 pb-3 pt-2 text-[11px] leading-relaxed text-gray-400 dark:text-gray-500">
        New step types appear here as soon as they are registered — no layout change needed.
      </p>
    </DropdownMenuContent>
  </DropdownMenu>
);

export default AddStepMenu;
